import { useEffect, useState, useRef } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { firestore } from '../../../lib/firebase/client';
import { useAuth } from '../../../app/providers/AuthProvider';
import type { AccountStatus } from '@penticton-dev-events/shared';

export interface AccountStatusData {
  accountStatus: AccountStatus | null;
  isActive: boolean;
  isPending: boolean;
  isSuspended: boolean;
  registrationMethod?: 'open' | 'invite' | 'approval';
  loading: boolean;
}

/**
 * Hook to check the current user's account status
 * Returns account status from Firestore user document
 * Automatically signs out users when their account is suspended/rejected
 */
export function useAccountStatus(): AccountStatusData {
  const { user, signOut } = useAuth();
  const [accountStatus, setAccountStatus] = useState<AccountStatus | null>(null);
  const [registrationMethod, setRegistrationMethod] = useState<'open' | 'invite' | 'approval'>();
  const [loading, setLoading] = useState(true);
  const previousStatusRef = useRef<AccountStatus | null>(null);

  useEffect(() => {
    if (!user?.uid) {
      setAccountStatus(null);
      setRegistrationMethod(undefined);
      setLoading(false);
      previousStatusRef.current = null;
      return;
    }

    const userDocRef = doc(firestore, 'users', user.uid);
    let unsubscribed = false;

    const unsubscribe = onSnapshot(
      userDocRef,
      snapshot => {
        if (unsubscribed) return; // Ignore updates after unsubscribe

        if (snapshot.exists()) {
          const data = snapshot.data();

          // Get account status, default to 'active' for backwards compatibility
          const status = (data.accountStatus as AccountStatus) || 'active';

          setAccountStatus(status);
          setRegistrationMethod(data.registrationMethod as 'open' | 'invite' | 'approval');

          // Auto sign-out if account becomes suspended
          if (status === 'suspended' && previousStatusRef.current !== 'suspended') {
            console.log('Account suspended - signing out user');
            unsubscribed = true; // Mark as unsubscribed before signing out
            void signOut();
          }

          previousStatusRef.current = status;
        } else {
          // User document doesn't exist yet, assume active for backwards compatibility
          setAccountStatus('active');
          previousStatusRef.current = 'active';
        }
        setLoading(false);
      },
      error => {
        // Ignore permission errors during sign-out (expected behavior)
        if (unsubscribed || error.code === 'permission-denied') {
          return;
        }
        console.error('Error fetching account status:', error);
        // Default to active on error
        setAccountStatus('active');
        previousStatusRef.current = 'active';
        setLoading(false);
      }
    );

    return () => {
      unsubscribed = true;
      unsubscribe();
    };
  }, [user?.uid, signOut]);

  return {
    accountStatus,
    isActive: accountStatus === 'active',
    isPending: accountStatus === 'pending',
    isSuspended: accountStatus === 'suspended',
    registrationMethod,
    loading,
  };
}
